import React, {useEffect, useCallback, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useUsid, useCustomerId} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '../../../../hooks/use-current-customer'
import {useSiteCustomPreferences} from '../../../../hooks/use-site-custom-preferences'
import {getCookieId, getPageType} from '../../../../utils/custom-utils'
import {getSessionJSONItem, clearSessionJSONItem} from '../../../../utils/utils'

export const SegmentContext = React.createContext()

export const SegmentProvider = ({segmentContext, children}) => {
    const configs = getConfig()
    const customerId = useCustomerId()
    const {getUsidWhenReady} = useUsid()
    const {locale} = useMultiSite()
    const appOrigin = useAppOrigin()
    const {data: customer} = useCurrentCustomer()
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const segmentEnabled = getcustomPreferencevalue('pwaSegmentEnabled')
    const segmentScript = getcustomPreferencevalue('pwaSegmentHeaderScript')
    const siteVersion = getcustomPreferencevalue('siteVersion')
    const pageType = getPageType()
    const cookieId = getCookieId()
    const customerEmail = customer?.email || ''
    const firstName = customer?.firstName || ''
    const lastName = customer?.lastName || ''
    const customerTier = customer?.c_subscriptionLevel || ''
    const [isSegmentReady, setIsSegmentReady] = useState(false)

    // Cache for the script content
    const scriptCache = useRef({content: null, isLoaded: false})

    const loadSegmentScript = useCallback(() => {
        if (!segmentScript || scriptCache.current.content === segmentScript) {
            return
        }

        const existingScript = document.getElementById('segment-analytics-script')
        if (existingScript) existingScript.remove()

        const scriptTag = document.createElement('script')
        scriptTag.type = 'text/javascript'
        scriptTag.text = segmentScript
        scriptTag.id = 'segment-analytics-script'
        scriptTag.defer = true
        document.head.appendChild(scriptTag)

        // Update cache
        scriptCache.current = {content: segmentScript, isLoaded: true}
    }, [segmentScript])

    useEffect(() => {
        if (!segmentContext?._isReady && segmentEnabled) {
            try {
                if (typeof window !== 'undefined' && !window.analytics) {
                    loadSegmentScript()
                    segmentContext.initialize()
                    setIsSegmentReady(true)
                }
            } catch (error) {
                logger.error('Segment intialization', {
                    namespace: 'segment-initalize',
                    additionalProperties: {error: error.message}
                })
            }
        }
    }, [segmentEnabled, segmentScript, segmentContext, loadSegmentScript])

    useEffect(() => {
        const isLogin = getSessionJSONItem('isLogin')

        if (isLogin && customer?.email && isSegmentReady) {
            try {
                const customerData = {
                    id: customerId || '',
                    email: customer.email,
                    firstName: customer.firstName || '',
                    lastName: customer.lastName || '',
                    plan: customer.c_subscriptionLevel || '',
                    cid: cookieId,
                    eventSource: configs.app.library.segmentEventSource,
                    shopRegion: locale?.id?.split('-')[1]?.toUpperCase() || ''
                }

                window.analytics.identify(customerData.id, customerData, {
                    path: typeof window !== 'undefined' ? window?.location?.pathname : ''
                })

                clearSessionJSONItem('isLogin')
            } catch (error) {
                logger.error('Segment identify failed', error)
            }
        }
    }, [customer?.email, isSegmentReady])

    const contextValue = useMemo(() => {
        const eventSource = configs.app.library.segmentEventSource
        const localeSplit = locale?.id?.split('-')
        const shopLanguage = localeSplit[0]
        const shopCountry = localeSplit[1].toLowerCase()
        const shopRegion = localeSplit[1].toUpperCase()
        const localeId = locale?.id
        const currency = locale?.preferredCurrency
        const context = {
            path: typeof window !== 'undefined' ? window?.location?.pathname : ''
        }

        return {
            eventSource,
            siteVersion,
            shopLanguage,
            shopCountry,
            shopRegion,
            localeId,
            currency,
            pageType,
            customerEmail,
            firstName,
            lastName,
            customerTier,
            context,
            getUsidWhenReady,
            customerId,
            cookieId,
            isSegmentReady,
            appOrigin
        }
    }, [segmentEnabled, locale, pageType, isSegmentReady, customerTier])

    return <SegmentContext.Provider value={contextValue}>{children}</SegmentContext.Provider>
}

SegmentProvider.propTypes = {
    children: PropTypes.node.isRequired,
    segmentContext: PropTypes.object
}
